import { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis
} from 'recharts';

const dialogPaperSx = {
    borderRadius: '22px',
    boxShadow: '0px 10px 30px rgba(61, 61, 61, 0.12)'
};

const dialogTitleSx = {
    px: 3,
    pt: 3,
    pb: 1.5,
    fontWeight: 700,
    color: '#3D3D3D'
};

const dialogContentSx = {
    px: 3,
    pb: 1,
    pt: 0.5
};

const dialogActionsSx = {
    px: 3,
    pb: 2.5,
    pt: 1,
    gap: 1
};

const secondaryDialogButtonSx = {
    minHeight: 48,
    px: 2.2,
    textTransform: 'none',
    fontWeight: 700,
    color: '#3D3D3D',
    borderColor: '#D9D9D9',
    borderWidth: '2px',
    borderRadius: '16px',
    '&:hover': {
        bgcolor: '#F7F7F7',
        borderColor: '#CFCFCF',
        borderWidth: '2px'
    }
};

const primaryDialogButtonSx = {
    minHeight: 48,
    px: 2.2,
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: '16px',
    bgcolor: '#3D3D3D',
    boxShadow: 'none',
    '&:hover': {
        bgcolor: '#3D3D3D',
        boxShadow: 'none'
    }
};

const toPtDateTime = (isoTs) => {
    if (!isoTs) return '--';
    const date = new Date(isoTs);
    if (Number.isNaN(date.getTime())) return isoTs;
    return date.toLocaleString('pt-PT');
};

const headerSx = {
    px: 2.2,
    py: 1.1,
    display: 'grid',
    gridTemplateColumns: '1.2fr 1.6fr 2.4fr 1fr',
    gap: 1.2,
    bgcolor: '#3D3D3D',
    color: '#fff',
    borderRadius: '10px',
    boxShadow: 'none'
};

const rowSx = {
    px: 2.2,
    py: 1.35,
    display: 'grid',
    gridTemplateColumns: '1.2fr 1.6fr 2.4fr 1fr',
    gap: 1.2,
    alignItems: 'center',
    bgcolor: '#ECECEC',
    borderRadius: '12px',
    boxShadow: 'none'
};

const AssessmentTable = ({
    rows,
    loading,
    onOpenNotes,
    onCreateNoteClick,
    pageIndex = 0,
    totalPages = 1,
    hasNextPage = false,
    onPrevPage,
    onNextPage
}) => (
    <Stack spacing={0.8}>
        <Typography variant="h6" fontWeight={700} mt={1} color="#3D3D3D">
            Notas
        </Typography>
        <Paper sx={headerSx}>
            <Typography variant="caption" fontWeight={600}>Avaliador</Typography>
            <Typography variant="caption" fontWeight={600}>Data</Typography>
            <Typography variant="caption" fontWeight={600}>Tipo</Typography>
            <Typography variant="caption" fontWeight={600}>Notas</Typography>
        </Paper>
        {loading ? (
            <Paper sx={rowSx}>
                <Typography color="text.secondary">A carregar notas...</Typography>
            </Paper>
        ) : null}
        {!loading && rows.length === 0 ? (
            <Paper sx={rowSx}>
                <Typography color="text.secondary">Sem notas para este cuidador.</Typography>
            </Paper>
        ) : null}
        {!loading
            ? rows.map((row) => (
                  <Paper key={row.appointmentUuid} sx={rowSx}>
                      <Typography variant="body2" fontWeight={700} color="#3D3D3D">
                          {row.evaluator}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="text.secondary">
                          {row.date}
                      </Typography>
                      <Stack direction="row" spacing={1.1} alignItems="center">
                          <Typography variant="body2" fontWeight={600} color="text.secondary">
                              {row.exam}
                          </Typography>
                          {row.file ? <VisibilityOutlinedIcon fontSize="small" /> : null}
                          {row.file ? <DownloadRoundedIcon fontSize="small" /> : null}
                      </Stack>
                      <Tooltip title="Ver nota">
                          <IconButton
                              size="small"
                              sx={{
                                  bgcolor: '#3D3D3D',
                                  color: '#fff',
                                  width: 34,
                                  height: 34,
                                  '&:hover': { bgcolor: '#3D3D3D' }
                              }}
                              onClick={() => onOpenNotes?.(row.appointmentUuid)}
                          >
                              <AssignmentOutlinedIcon fontSize="small" />
                          </IconButton>
                      </Tooltip>
                  </Paper>
              ))
            : null}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Tooltip title="Criar nova nota">
                <IconButton
                    sx={{
                        bgcolor: '#3D3D3D',
                        color: '#fff',
                        width: 34,
                        height: 34,
                        '&:hover': { bgcolor: '#3D3D3D' }
                    }}
                    onClick={onCreateNoteClick}
                >
                    <AddRoundedIcon />
                </IconButton>
            </Tooltip>
            <Stack direction="row" spacing={0.75} alignItems="center">
                <Tooltip title="Página anterior">
                    <span>
                        <IconButton
                            size="small"
                            onClick={onPrevPage}
                            disabled={pageIndex === 0}
                            sx={{ color: pageIndex === 0 ? '#b8b8b8' : '#5a5a5a' }}
                        >
                            <ChevronLeftRoundedIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Typography color="text.secondary">{`${pageIndex + 1} de ${totalPages}`}</Typography>
                <Tooltip title="Página seguinte">
                    <span>
                        <IconButton
                            size="small"
                            onClick={onNextPage}
                            disabled={!hasNextPage}
                            sx={{ color: hasNextPage ? '#5a5a5a' : '#b8b8b8' }}
                        >
                            <ChevronRightRoundedIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
        </Stack>
    </Stack>
);

const EmptyChart = () => (
    <Box
        sx={{
            width: '80%',
            height: 280,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#f2f2f2',
            borderRadius: 2
        }}
    >
        <Typography variant="body2" color="text.secondary">
            Sem dados
        </Typography>
    </Box>
);

const normalizeMoodState = (value) =>
    String(value || '')
        .trim()
        .toLowerCase();

const moodStateToScore = (state) => {
    const normalized = normalizeMoodState(state);

    if (normalized === 'excelente') return 7;
    if (normalized === 'muito bem') return 6;
    if (normalized === 'bem') return 5;
    if (normalized === 'calmo') return 5;
    if (normalized === 'razoavel' || normalized === 'razoável') return 4;
    if (normalized === 'triste') return 2;
    if (normalized === 'mal') return 2;

    return null;
};

const formatMoodStateLabel = (value) => {
    const text = String(value || '').trim();
    if (!text) {
        return 'Sem registo';
    }
    return text.charAt(0).toUpperCase() + text.slice(1);
};

const formatShortPtDateTime = (isoTs) => {
    if (!isoTs) return '--';
    const date = new Date(isoTs);
    if (Number.isNaN(date.getTime())) return '--';
    return date.toLocaleString('pt-PT', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const buildMoodChartData = (measurements = []) =>
    [...measurements]
        .map((item) => {
            const rawScore = item?.data?.score;
            const numericScore =
                typeof rawScore === 'number'
                    ? rawScore
                    : typeof rawScore === 'string' && rawScore.trim() !== ''
                        ? Number(rawScore)
                        : moodStateToScore(item?.data?.state);

            return {
                id: item?.uuid,
                label: formatShortPtDateTime(item?.ts),
                score: Number.isFinite(numericScore) ? numericScore : null,
                state: formatMoodStateLabel(item?.data?.state)
            };
        })
        .filter((item) => item.score != null)
        .reverse()
        .slice(-8);

const MoodChart = ({ measurements }) => {
    const data = useMemo(() => buildMoodChartData(measurements), [measurements]);

    if (!data.length) {
        return <EmptyChart />;
    }

    return (
        <Box sx={{ width: '100%', height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 8, right: 12, left: -22, bottom: 0 }}>
                    <CartesianGrid stroke="#E7E7E7" strokeDasharray="3 3" vertical={false} />
                    <XAxis
                        dataKey="label"
                        tick={{ fill: '#7A7A7A', fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                    />
                    <YAxis
                        domain={[1, 7]}
                        ticks={[1, 2, 3, 4, 5, 6, 7]}
                        tick={{ fill: '#7A7A7A', fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        width={28}
                    />
                    <RechartsTooltip
                        formatter={(value, _name, item) => [`${value}`, item?.payload?.state || '']}
                        labelFormatter={(label) => label}
                        contentStyle={{
                            borderRadius: 12,
                            border: '1px solid #E4E4E4',
                            boxShadow: '0px 8px 20px rgba(61, 61, 61, 0.08)'
                        }}
                    />
                    <Line
                        type="monotone"
                        dataKey="score"
                        stroke="#D63731"
                        strokeWidth={2.5}
                        dot={{ r: 3, fill: '#D63731' }}
                        activeDot={{ r: 5 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </Box>
    );
};

const Block = ({
    title,
    leftFields,
    moodMeasurements,
    rows,
    loading,
    onOpenNotes,
    onCreateNoteClick,
    showTable = true,
    pageIndex,
    totalPages,
    hasNextPage,
    onPrevPage,
    onNextPage
}) => (
    <Box>
        <Stack
            direction="row"
            spacing={2}
            alignItems="flex-start"
            mb={2}
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '0.58fr 0.42fr' } }}
        >
            <Box>
                <Typography variant="h4" fontWeight={700} mb={6}>
                    {title}
                </Typography>
                <Stack spacing={6} sx={{ maxWidth: 560 }}>
                    {leftFields.map((f) => (
                        <TextField key={f.label} label={f.label} value={f.value} disabled />
                    ))}
                </Stack>
            </Box>
            <MoodChart measurements={moodMeasurements} />
        </Stack>
        {showTable ? (
            <AssessmentTable
                rows={rows}
                loading={loading}
                onOpenNotes={onOpenNotes}
                onCreateNoteClick={onCreateNoteClick}
                pageIndex={pageIndex}
                totalPages={totalPages}
                hasNextPage={hasNextPage}
                onPrevPage={onPrevPage}
                onNextPage={onNextPage}
            />
        ) : null}
    </Box>
);

const mapNotesToRows = (notes) =>
    notes.map((item) => ({
        appointmentUuid: item.uuid,
        evaluator: item?.author?.name || 'Sistema',
        date: toPtDateTime(item.ts || item.created),
        exam: item?.appointment ? 'Consulta associada' : 'Nota manual',
        file: false
    }));

const CaregiverAssessmentsSection = ({
    caregiverName,
    patientName,
    appointments,
    loadingAppointments,
    appointmentsPageIndex,
    appointmentsTotalPages = 1,
    hasNextAppointmentsPage,
    onPrevAppointmentsPage,
    onNextAppointmentsPage,
    onOpenNotes,
    onCreateNote,
    creatingNote,
    createNoteError,
    canCreatePrivateNotes = false,
    caregiverMoodMeasurements = [],
    patientMoodMeasurements = []
}) => {
    const rows = useMemo(() => mapNotesToRows(appointments || []), [appointments]);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newNote, setNewNote] = useState('');
    const [newNotePriviledged, setNewNotePriviledged] = useState(false);

    const caregiverMoodRows = useMemo(() => {
        if (caregiverMoodMeasurements?.length) {
            return caregiverMoodMeasurements;
        }

        return (patientMoodMeasurements || []).filter(
            (item) => normalizeMoodState(item?.data?.target) === 'caregiver'
        );
    }, [caregiverMoodMeasurements, patientMoodMeasurements]);

    const patientMoodRows = useMemo(
        () =>
            (patientMoodMeasurements || []).filter((item) => {
                const target = normalizeMoodState(item?.data?.target);
                return !target || target === 'patient';
            }),
        [patientMoodMeasurements]
    );

    const latestCaregiverMood = caregiverMoodRows[0] || null;
    const latestPatientMood = patientMoodRows[0] || null;

    const handleOpenCreateDialog = () => {
        setNewNote('');
        setNewNotePriviledged(false);
        setCreateDialogOpen(true);
    };

    const handleCloseCreateDialog = () => {
        if (creatingNote) return;
        setCreateDialogOpen(false);
    };

    const handleCreate = async () => {
        const content = newNote.trim();
        if (!content || !onCreateNote) return;
        const ok = await onCreateNote(content, { priviledged: newNotePriviledged });
        if (ok) {
            setCreateDialogOpen(false);
            setNewNote('');
            setNewNotePriviledged(false);
        }
    };

    return (
        <Stack spacing={4}>
            <Block
                title={caregiverName}
                leftFields={[
                    {
                        label: 'Niveis de Sobrecarga',
                        value: formatMoodStateLabel(latestCaregiverMood?.data?.state)
                    },
                    {
                        label: 'Último Registo',
                        value: formatShortPtDateTime(latestCaregiverMood?.ts)
                    }
                ]}
                moodMeasurements={caregiverMoodRows}
                rows={rows}
                loading={loadingAppointments}
                onOpenNotes={onOpenNotes}
                onCreateNoteClick={handleOpenCreateDialog}
                pageIndex={appointmentsPageIndex}
                totalPages={appointmentsTotalPages}
                hasNextPage={hasNextAppointmentsPage}
                onPrevPage={onPrevAppointmentsPage}
                onNextPage={onNextAppointmentsPage}
            />

            <Box sx={{ borderTop: '2px solid #bdbdbd', pt: 4 }}>
                <Block
                    title={`Pessoa a Cuidados: ${patientName || '--'}`}
                    leftFields={[
                        {
                            label: 'Niveis de Sobrecarga',
                            value: formatMoodStateLabel(latestPatientMood?.data?.state)
                        },
                        {
                            label: 'Último Registo',
                            value: formatShortPtDateTime(latestPatientMood?.ts)
                        }
                    ]}
                    moodMeasurements={patientMoodRows}
                    showTable={false}
                />
            </Box>
            <Dialog
                open={createDialogOpen}
                onClose={handleCloseCreateDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Criar nova nota</DialogTitle>
                <DialogContent sx={dialogContentSx}>
                    {createNoteError ? <Alert severity="error">{createNoteError}</Alert> : null}
                    <TextField
                        sx={{ mt: 1 }}
                        fullWidth
                        multiline
                        minRows={5}
                        label="Nota"
                        placeholder="Escreve aqui a nota..."
                        value={newNote}
                        onChange={(event) => setNewNote(event.target.value)}
                    />
                    {canCreatePrivateNotes ? (
                        <FormControlLabel
                            sx={{ mt: 1 }}
                            control={
                                <Switch
                                    checked={newNotePriviledged}
                                    onChange={(event) => setNewNotePriviledged(event.target.checked)}
                                    color="error"
                                />
                            }
                            label="Nota privada"
                        />
                    ) : null}
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button
                        onClick={handleCloseCreateDialog}
                        disabled={creatingNote}
                        variant="outlined"
                        sx={secondaryDialogButtonSx}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreate}
                        disabled={creatingNote || !newNote.trim()}
                        sx={primaryDialogButtonSx}
                    >
                        {creatingNote ? 'A guardar...' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default CaregiverAssessmentsSection;



